/**
 * Paylaşılan **yığılmış oran çubuğu** (Reuse Mandate). Grafik motoru değil; flex + yüzde genişlik.
 *
 * İki kullanım modu:
 * - **`max` verilmeden** (varsayılan): segmentler kendi aralarında orantılanır (`flex-grow`) ve çubuk
 *   tamamen dolar — durum dağılımı çubukları böyle çalışır.
 * - **`max` verilerek**: segmentler ortak bir tabana göre ölçeklenir, kalan kısım boş ray olarak durur —
 *   satırların birbiriyle **karşılaştırılabilir** olması gerektiğinde (ör. kişi bazlı yük) budur.
 *
 * `max = 0` iken bölme yapılmaz (tüm barlar boş ray). Renk yalnız çağıranın verdiği token'lardan gelir.
 * Çubuk `role="img"` + `ariaLabel` taşır; aynı bilgi çağıran tarafında **metin** olarak da bulunur, yani
 * renk tek bilgi taşıyıcısı değildir.
 */
export interface StackedBarSegment {
  key: string
  label: string
  value: number
  /** CSS renk değeri (token). */
  color: string
  /**
   * `showValues` açıkken segmentin içine yazılacak metin; verilmezse ham `value` yazılır.
   * Yönetici Raporu buraya "adet (%oran)" koyar (079) — biçimlendirme ve oranın paydası çağıranın
   * kararıdır, bileşen yüzde hesaplamaz.
   *
   * <p>Metin **kısaltılmaz**: segment bu metni taşıyacak taban genişliği CSS'ten alır
   * (bkz. {@link StackedBarProps.showValues}).
   */
  valueText?: string
}

export interface StackedBarProps {
  segments: StackedBarSegment[]
  /** Ortak ölçek tabanı; verilirse tüm satırlarda AYNI değer geçilmelidir. */
  max?: number
  ariaLabel: string
  className?: string
  /**
   * Segment değerini çubuğun **içine** yazar (077 mockup'ı). Metin `aria-hidden`'dır — aynı bilgi zaten
   * `ariaLabel`'da ve çağıranın satır özetinde bulunur; iki kez okutmak gürültü olurdu.
   *
   * <p><b>Metin HER segmentte tam yazılır</b> (kullanıcı kararı 2026-08-17: "tüm barlarda yüzdelik
   * değer de gözüksün"). Bunu sağlayan tek mekanizma CSS'tir: `--values` modunda segment
   * `min-width: max-content` alır → kendi metnini taşıyacak taban genişliğin altına inemez, dolayısıyla
   * ne kısaltma ne kırpma olur (JS ölçümü de yok). Bedeli: değeri küçük segmentler olduğundan geniş
   * çizilir; gerekçesi ve ölçülen sınırı `ui.css`te.
   *
   * <p>Önceki iki davranış da bilgi kaybediyordu: ilk sürüm metni `overflow: hidden` ile <b>kırpıyor</b>
   * (dar segmentte yarım sayı), 086 ise dar segmentte oranı <b>düşürüp</b> yalnız adedi yazıyordu — oran
   * o durumda sadece `title` ipucunda kalıyordu, yani dokunmatikte hiç okunamıyordu.
   */
  showValues?: boolean
}

export default function StackedBar({
  segments,
  max,
  ariaLabel,
  className,
  showValues = false,
}: Readonly<StackedBarProps>) {
  const drawn = segments.filter((s) => s.value > 0)
  const scaled = max != null

  return (
    <div
      className={`ui-bar${scaled ? ' ui-bar--scaled' : ''}${showValues ? ' ui-bar--values' : ''}${className ? ` ${className}` : ''}`}
      role="img"
      aria-label={ariaLabel}
    >
      {drawn.map((segment) => (
        <span
          key={segment.key}
          className="ui-bar-seg"
          // Ölçekli modda genişlik yüzdeyle verilir; `.ui-bar--scaled` segmentin varsayılan
          // `flex-basis: 0` değerini `auto`ya çeker. Aksi hâlde flex düzeninde `flex-basis`,
          // `width`i geçersiz kılar ve segment SIFIR genişlikte çizilir (çubuk hiç dolmaz).
          //
          // Değer yazan modun taban genişliği burada DEĞİL, CSS'te (`min-width: max-content`) verilir:
          // metnin gerçek genişliğini tarayıcı ölçer, hane sayısından tahmin etmek gerekmez.
          style={
            scaled
              ? { width: max > 0 ? `${(segment.value / max) * 100}%` : '0%' }
              : { flexGrow: segment.value }
          }
          title={`${segment.label}: ${segment.valueText ?? segment.value}`}
        >
          <span className="ui-bar-fill" style={{ background: segment.color }}>
            {showValues && (
              <span className="ui-bar-value" aria-hidden="true">
                {segment.valueText ?? segment.value}
              </span>
            )}
          </span>
        </span>
      ))}
    </div>
  )
}
